import React, { useState, useEffect } from 'react';
import ProductCard from './ProductCard';
import CoverDetails from './CoverDetails';
import InsuredDetails from './InsuredDetails';
import VehicleDetails from './VehicleDetails';
import Checkout from './Checkout';
import ExtendPolicyFinder from './ExtendPolicyFinder';
import Receipt from './Receipt';
import Snackbar from './Snackbar';
import { getApiBaseUrl } from '../config/api';
import { buildPricingFromRates, amountFromTonnageRanges, grossFromNet } from '../utils/productRatesApi';
import {
  buildAgentTransactionContext,
  persistAgentTransactionContext,
  getPersistedAgentTransactionContext,
  clearAgentTransactionContext,
} from '../utils/agentTransactionContext';
import './CoverPurchase.css';
import { fetchExtensionSeed, EXTENSION_SEED_USER_MESSAGE } from '../utils/extensionSeed';
import {
  inferExtendableVehicleClass,
  resolveExtensionVehicleClass
} from '../utils/extendPolicyInference';
import { tpoPricingLookupLabel } from '../utils/tpoPricingLookup';
import { getInstitutionalPllFee } from '../utils/institutionalPll';
import {
  TPO_EXTEND_VEHICLE_CLASSES,
  getTpoExtendAnnualPremiumFactor,
  mapAnnualTpoRowsToFreshExtendMonthDisplay,
  mapAnnualTpoRowsToSecondHalfExtendDisplay
} from '../utils/tpoExtendRates';
import {
  inclusiveEndForCoverPeriod,
  compareYmd,
  getTodayYmdLocal,
  getTpoExtendEarliestStartYmd
} from '../utils/calendarDates';
import { tpoPremiumLookupPeriod } from '../utils/coverPeriodTerms';
import { ENABLED_VEHICLE_CLASSES } from '../utils/comprehensiveRates';

// Helper function to get full API URL
const getApiUrl = (path) => {
  const baseUrl = getApiBaseUrl();
  return `${baseUrl}${path}`;
};

const CoverPurchase = ({
  agentId,
  agency,
  agentUserId,
  issuedByAdminId,
  user = null,
  isDirectBusiness = false,
  comprehensiveOnly = false,
  initialEntryStep = 'product-selection',
  onBehalfOfLabel,
  renewalSeed = null,
  onClose,
  onPurchaseComplete
}) => {
  const renewalCover = String(renewalSeed?.coverType || '').toUpperCase();
  const renewalCoverOk = renewalCover === 'TPO' || renewalCover === 'TOR';
  const [selectedCover, setSelectedCover] = useState(renewalCoverOk ? renewalCover : null);
  const [selectedClass, setSelectedClass] = useState(renewalCoverOk ? (renewalSeed.vehicleClass || null) : null);
  const [institutionType, setInstitutionType] = useState(null);
  const [startDate, setStartDate] = useState(renewalCoverOk ? (renewalSeed.startDate || '') : '');
  const [coverPeriod, setCoverPeriod] = useState(
    renewalCoverOk ? (renewalSeed.coverPeriod || (renewalCover === 'TOR' ? '1 Month' : '1 Year')) : ''
  );
  const [endDate, setEndDate] = useState(renewalCoverOk ? (renewalSeed.endDate || '') : '');
  const [currentStep, setCurrentStep] = useState(
    renewalSeed?.coverType ? 'cover-details' : initialEntryStep
  );
  const [insuredData, setInsuredData] = useState(renewalCoverOk ? (renewalSeed.insured || null) : null);
  const [vehicleData, setVehicleData] = useState(renewalCoverOk ? (renewalSeed.vehicle || null) : null);
  const [insuranceCheckResult, setInsuranceCheckResult] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [extensionContext, setExtensionContext] = useState(null);
  const [extensionHydrationError, setExtensionHydrationError] = useState(null);
  
  // Snackbar state
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('info');

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  };

  // Show notification function
  const showNotification = (message, type = 'info', duration = 5000) => {
    setSnackbarMessage(message);
    setSnackbarType(type);
    setShowSnackbar(true);
  };

  // Set agent transaction context when agent (or underwriter) starts purchase-cover flow so the business is linked to them
  const [agentTransactionContext, setAgentTransactionContext] = useState(null);
  useEffect(() => {
    const context = buildAgentTransactionContext({
      agentId,
      agency,
      agentUserId,
      issuedByAdminId,
      isDirectBusiness
    });
    if (context) {
      setAgentTransactionContext(context);
      persistAgentTransactionContext(context);
    } else {
      const persisted = getPersistedAgentTransactionContext();
      if (persisted) setAgentTransactionContext(persisted);
    }
  }, [agentId, agency, agentUserId, issuedByAdminId, isDirectBusiness]);

  // Scroll to top whenever step changes
  useEffect(() => {
    scrollToTop();
  }, [currentStep]);

  // Renewal: prefill from the latest TPO/TOR policy and start on cover details.
  useEffect(() => {
    const cover = String(renewalSeed?.coverType || '').toUpperCase();
    if (cover !== 'TPO' && cover !== 'TOR') return;
    setSelectedCover(cover);
    setSelectedClass(renewalSeed.vehicleClass || '');
    setInstitutionType(null);
    setStartDate(renewalSeed.startDate || '');
    setCoverPeriod(renewalSeed.coverPeriod || (cover === 'TOR' ? '1 Month' : '1 Year'));
    setEndDate(renewalSeed.endDate || '');
    setInsuredData(renewalSeed.insured || null);
    setVehicleData(renewalSeed.vehicle || null);
    setInsuranceCheckResult(null);
    setClientId(null);
    setExtensionContext(null);
    setCurrentStep('cover-details');
  }, [renewalSeed]);

  const tpoClasses = [
    'Motor Private',
    'Motor Commercial Institutional',
    'Motor Commercial General Cartage',
    'Motor Commercial Own Goods',
    'Tractor TPO',
    'Tuk Tuk Commercial',
    'Tuk Tuk PSV',
    'Prime Movers',
    'Motorcycle Private',
    'Motorcycle PSV Annual',
    'Motorcycle PSV 6 Months',
    'PSV Chauffer Driven'
  ];

  const compClasses = tpoClasses.filter((c) =>
    ENABLED_VEHICLE_CLASSES.some((e) => e.toLowerCase() === c.toLowerCase())
  );

  // Production: COMP available to agents and underwriters (dedicated UW flow or alongside TPO/TOR)
  const showComprehensiveTile = true;

  const torClasses = [
    'Motor Private',
    'Motor Commercial',
    'PSV Chauffer Driven',
    'Motorcycle PSV'
  ];
  const tpoExtendClasses = TPO_EXTEND_VEHICLE_CLASSES;

  // Product rates: source of truth is /api/product-rates (product_rates in DB, synced from DMVIC Excel).
  // Fallback below matches current DMVIC rates (gross = customer pays) for initial paint / API failure.
  const [tpoPricing, setTpoPricing] = useState([
    { label: 'Motor Private', amount: 3300, period: '1 Year' },
    { label: 'Motor Commercial Institutional', amount: 4155, period: '1 Year', tonnageBased: true },
    { label: 'Motor Commercial General Cartage', amount: 4155, period: '1 Year', tonnageBased: true },
    { label: 'Motor Commercial Own Goods', amount: 4155, period: '1 Year', tonnageBased: true },
    { label: 'Prime Movers', amount: 9185, period: '1 Year' },
    { label: 'Motorcycle Private', amount: 1870, period: '1 Year' },
    { label: 'Motorcycle PSV', amount: 3241, period: '1 Year' },
    { label: 'Motorcycle PSV', amount: 1870, period: '6 Months' },
    { label: 'Tractor TPO', amount: 2784, period: '1 Year' },
    { label: 'Tuk Tuk Commercial', amount: 2784, period: '1 Year' },
    { label: 'Tuk Tuk PSV', amount: 4155, period: '1 Year', psvPricing: true, passengerFee: 0 },
    { label: 'PSV Chauffer Driven', amount: 5500, period: '1 Year', psvPricing: true }
  ]);
  const [torPricing, setTorPricing] = useState([
    { label: 'Motor Private', amount: 600, period: '1 Month' },
    { label: 'Motor Commercial', amount: 600, period: '1 Month' },
    // TOR PSV Chauffer Driven underwriting-aligned defaults:
    // PLL 3 => 1346, PLL 4 => 1441 (base 1061 + 95 per passenger)
    { label: 'PSV Chauffer Driven', amount: 1061, period: '1 Month', passengerFee: 95 },
    { label: 'Motorcycle PSV', amount: 503, period: '1 Month' }
  ]);
  const tpoExtendPricing = React.useMemo(
    () => mapAnnualTpoRowsToSecondHalfExtendDisplay(tpoPricing, tpoExtendClasses),
    [tpoPricing, tpoExtendClasses]
  );

  const tpoExtendFreshMonthPricing = React.useMemo(
    () => mapAnnualTpoRowsToFreshExtendMonthDisplay(tpoPricing, tpoExtendClasses),
    [tpoPricing, tpoExtendClasses]
  );

  useEffect(() => {
    fetch(getApiUrl('/api/product-rates'))
      .then(res => res.json())
      .then(data => {
        if (data.success && data.rates && data.rates.length > 0) {
          const built = buildPricingFromRates(data.rates);
          if (built && (built.tpoPricing.length > 0 || built.torPricing.length > 0)) {
            if (built.tpoPricing.length > 0) setTpoPricing(built.tpoPricing);
            if (built.torPricing.length > 0) setTorPricing(built.torPricing);
          }
        }
      })
      .catch(() => {});
  }, []);

  // Function to calculate PSV pricing (TPO 1 Year). PLL = 500 per passenger.
  const calculatePSVPricing = (baseAmount, passengerCount) => {
    const n = parseInt(passengerCount, 10) || 0;
    if (n <= 0) return baseAmount;
    return baseAmount + (n * 500);
  };

  // Fallback Motor Commercial pricing (DMVIC tonnage bands, gross). Prefer API tonnage_ranges when available.
  const calculateCommercialPricing = (tonnage, bodyType = null) => {
    const tonnageValue = parseFloat(tonnage);
    if (bodyType && bodyType.toUpperCase() === 'PRIME MOVER') {
      return 9185; // DMVIC Prime Movers (rate includes levies = client pays)
    }
    // DMVIC bands: amounts include levies = what client pays (no conversion)
    if (tonnageValue <= 3) return 4155;
    if (tonnageValue <= 8) return 5070;
    if (tonnageValue <= 12) return 5984;
    if (tonnageValue <= 15) return 6899;
    if (tonnageValue <= 20) return 9185;
    return 13758;
  };

  // TPO: 1 Year only — initial values above; replaced by API fetch when available
  // (tpoPricing / torPricing are state)

  // Calculate end date based on start date and period
  const calculateEndDate = (start, period) => inclusiveEndForCoverPeriod(start, period);

  // Update end date when start date or period changes (TPO_EXTEND: end pinned to original policy expiry).
  useEffect(() => {
    if (selectedCover === 'TPO_EXTEND' && extensionContext?.annualEndDate) {
      setEndDate(extensionContext.annualEndDate);
      return;
    }
    if (startDate && coverPeriod) {
      const calculatedEndDate = calculateEndDate(startDate, coverPeriod);
      setEndDate(calculatedEndDate);
    }
  }, [startDate, coverPeriod, selectedCover, extensionContext?.annualEndDate]);

  // Internal pricing calculation function
  const calculatePremium = (coverType, selectedClass, coverPeriod, tonnage = null, passengerCount = null, bodyType = null, institutionTypeArg = null) => {
    if (coverType === 'COMP') {
      const q = vehicleData?.comprehensiveQuote;
      if (q && Number(q.payableAmount) > 0) {
        return {
          amount: Number(q.payableAmount),
          period: '1 Month',
          description: selectedClass,
          annualTotal: Number(q.annualTotal) || 0,
          grossAmount: Number(q.payableAmount),
          comprehensiveQuote: q
        };
      }
      return null;
    }
    const isExtendProduct = coverType === 'TPO_EXTEND';
    const isSecondHalfExtension = isExtendProduct && !!extensionContext?.policyNo;
    let pricingData = (coverType === 'TPO' || isExtendProduct) ? tpoPricing : torPricing;
    /** Extend display tables already include 50%; plain TPO uses full annual × factor. */
    let premiumAmountFactor = 1;

    if (coverType === 'TPO' || isExtendProduct) {
      let lookupPeriod;
      if (isExtendProduct) {
        pricingData = isSecondHalfExtension ? tpoExtendPricing : tpoExtendFreshMonthPricing;
        lookupPeriod = isSecondHalfExtension ? '1 Year (50%)' : '1 Month';
        premiumAmountFactor = 1;
      } else {
        premiumAmountFactor = getTpoExtendAnnualPremiumFactor(coverType);
        lookupPeriod = tpoPremiumLookupPeriod(coverType, coverPeriod, selectedClass, {});
      }
      const rateLabel = tpoPricingLookupLabel(selectedClass);
      let matchingPrice = pricingData.find(
        (price) => price.label === rateLabel && price.period === lookupPeriod
      );
      if (!matchingPrice && isExtendProduct) {
        const annualRow = tpoPricing.find(
          (price) => price.label === rateLabel && price.period === '1 Year'
        );
        if (annualRow) {
          matchingPrice = {
            ...annualRow,
            period: lookupPeriod,
            amount: Math.round(Number(annualRow.amount || 0) * 0.5)
          };
        }
      }

      // User-selected period wins — never return a fallback rate row's period (e.g. 1 Year for 6 Months).
      const pricedPeriod = isSecondHalfExtension
        ? '1 Year (50%)'
        : isExtendProduct
          ? '1 Month'
          : lookupPeriod;
      
      if (matchingPrice && matchingPrice.tonnageBased && (tonnage != null && tonnage !== '' || (selectedClass === 'Motor Commercial Institutional' && passengerCount != null))) {
        // Tonnage-based: use API tonnage_ranges if available, else fallback to hardcoded bands.
        // Motor Commercial Institutional: base 5000 (≤14 pax) or 7500 (>14 pax) GROSS + PLL 250/pax (DMVIC: amounts are already gross, no levies).
        // TPO_EXTEND: extend tables only pre-discount flat rows — dynamic tonnage/institutional still needs ×50%.
        const tonnagePremiumFactor = isExtendProduct
          ? getTpoExtendAnnualPremiumFactor('TPO_EXTEND')
          : premiumAmountFactor;
        const coverTypeForTonnage = matchingPrice.coverType || coverType;
        const fromApi = matchingPrice.tonnageRanges && amountFromTonnageRanges(matchingPrice.tonnageRanges, tonnage, coverTypeForTonnage, matchingPrice.isNet);
        const tonnageAmount = fromApi != null ? fromApi : calculateCommercialPricing(tonnage, bodyType);
        const nPax = parseInt(passengerCount, 10) || 0;
        const pllFee = getInstitutionalPllFee(institutionTypeArg);
        const institutionalBase = selectedClass === 'Motor Commercial Institutional'
          ? (nPax > 14 ? 7500 : 5000)
          : 0;
        const institutionalPll = selectedClass === 'Motor Commercial Institutional'
          ? nPax * pllFee
          : 0;
        const grossAnnual = (selectedClass === 'Motor Commercial Institutional'
          ? (institutionalBase + institutionalPll)
          : (tonnageAmount + institutionalPll));
        return {
          amount: Math.round(grossAnnual * tonnagePremiumFactor),
          period: pricedPeriod,
          description: selectedClass,
          tonnageBased: selectedClass !== 'Motor Commercial Institutional',
          tonnage: tonnage,
          bodyType: bodyType,
          ...(selectedClass === 'Motor Commercial Institutional' && passengerCount != null && { passengerCount, institutionalPllFee: pllFee, institutionType: institutionTypeArg || 'school' })
        };
      }
      
      if (matchingPrice && matchingPrice.psvPricing) {
        let feePerPax = matchingPrice.passengerFee != null ? Number(matchingPrice.passengerFee) : 0;
        if (feePerPax === 0 && selectedClass === 'PSV Chauffer Driven') feePerPax = 500;
        const n = parseInt(passengerCount, 10) || 0;
        const annualAmount = matchingPrice.amount + n * feePerPax;
        if (feePerPax <= 0) {
          return {
            amount: Math.round(Number(matchingPrice.amount || 0) * premiumAmountFactor),
            period: pricedPeriod,
            description: selectedClass,
            psvPricing: true,
            passengerCount: passengerCount,
            baseAmount: matchingPrice.amount,
            passengerFee: 0,
            passengerFeePerPax: 0
          };
        }
        const psvAmount = Math.round(annualAmount * premiumAmountFactor);
        const pllTotal = n > 0 ? n * feePerPax : 0;
        return {
          amount: psvAmount,
          period: pricedPeriod,
          description: selectedClass,
          psvPricing: true,
          passengerCount: passengerCount,
          baseAmount: matchingPrice.amount,
          passengerFee: pllTotal,
          passengerFeePerPax: feePerPax
        };
      }
      
      return matchingPrice
        ? {
            ...matchingPrice,
            amount: Math.round(Number(matchingPrice.amount || 0) * premiumAmountFactor),
            period: pricedPeriod
          }
        : {
            amount: 0,
            period: pricedPeriod,
            description: selectedClass,
            rateMissing: true
          };
    } else {
      // TOR: add PLL when rate has passengerFee (e.g. PSV Chauffer Driven 100/pax)
      const matchingPrice = pricingData.find(price => price.label === selectedClass);
      if (!matchingPrice) return { amount: 0, period: '1 Month', description: selectedClass };
      const baseAmount = matchingPrice.amount ?? 0;
      const pllFee = matchingPrice.passengerFee != null ? Number(matchingPrice.passengerFee) : 0;
      const n = parseInt(passengerCount, 10) || 0;
      const total = pllFee > 0 && n > 0 ? baseAmount + (n * pllFee) : baseAmount;
      return {
        amount: total,
        period: '1 Month',
        description: selectedClass,
        baseAmount,
        passengerFee: pllFee > 0 ? n * pllFee : 0,
        passengerCount: n,
        passengerFeePerPax: pllFee
      };
    }
  };

  const handleCoverSelection = (cover) => {
    setExtensionContext(null);
    setSelectedCover(cover);
    if (cover === 'TPO_EXTEND') {
      setCoverPeriod('1 Month');
      setStartDate('');
      setEndDate('');
      setSelectedClass(null);
      setInstitutionType(null);
      setInsuredData(null);
      setVehicleData(null);
    } else if (cover === 'TPO') {
      setCoverPeriod('1 Year');
      setStartDate('');
      setEndDate('');
      setSelectedClass(null);
      setInstitutionType(null);
      setInsuredData(null);
      setVehicleData(null);
    } else if (cover === 'TOR' || cover === 'COMP') {
      setCoverPeriod('1 Month');
      setStartDate('');
      setEndDate('');
      setSelectedClass(null);
      setInstitutionType(null);
      setInsuredData(null);
      setVehicleData(null);
    }
    setCurrentStep('cover-details');
  };

  const handleCoverDetailsComplete = (classData, startDateData, periodData, institutionTypeData = null) => {
    setSelectedClass(classData);
    setInstitutionType(classData === 'Motor Commercial Institutional' ? institutionTypeData : null);
    setStartDate(startDateData);
    setCoverPeriod(periodData);
    const nextEnd =
      selectedCover === 'TPO_EXTEND' && extensionContext?.annualEndDate
        ? extensionContext.annualEndDate
        : calculateEndDate(startDateData, periodData);
    setEndDate(nextEnd);
    if (selectedCover === 'TPO_EXTEND' && extensionContext?.policyNo) {
      setCurrentStep('extension-loading');
      return;
    }
    setCurrentStep('insured-details');
  };

  const handleInsuredDetailsComplete = (insuredDetails) => {
    setInsuredData(insuredDetails);
    setCurrentStep('vehicle-details');
  };

  const handleVehicleDetailsComplete = (vehicleDetails, insuranceResult, nextAvailableDate = null) => {
    const withInstitution =
      selectedClass === 'Motor Commercial Institutional' && institutionType
        ? { ...vehicleDetails, institutionType }
        : vehicleDetails;
    setVehicleData(withInstitution);
    setInsuranceCheckResult(insuranceResult);
    if (nextAvailableDate) {
      setStartDate(nextAvailableDate);
      if (coverPeriod) {
        if (selectedCover === 'TPO_EXTEND' && extensionContext?.annualEndDate) {
          setEndDate(extensionContext.annualEndDate);
        } else {
          setEndDate(calculateEndDate(nextAvailableDate, coverPeriod));
        }
      }
    }
    setCurrentStep('checkout');
  };

  const handleProceedFromCoreSearch = async ({
    registrationNumber: _registrationNumber,
    inferredClass,
    startDate: transactionStartDate,
    selectedPolicy
  }) => {
    const sourcePolicyNo = selectedPolicy?.policyNo || selectedPolicy?.policy_number || null;
    if (!sourcePolicyNo) {
      showNotification(EXTENSION_SEED_USER_MESSAGE, 'error');
      throw new Error(EXTENSION_SEED_USER_MESSAGE);
    }
    const seed = await fetchExtensionSeed(getApiUrl, sourcePolicyNo);
    const portalAnnualEnd =
      (seed.computedAnnualEndDate && String(seed.computedAnnualEndDate).trim()) ||
      (seed.firstSegmentStartDate && inclusiveEndForCoverPeriod(seed.firstSegmentStartDate, '1 Year')) ||
      '';
    if (!portalAnnualEnd) {
      const msg =
        'This policy has no recorded first-segment start date in the portal, so the annual policy end cannot be computed (first issuance + 1 year). Extensions cannot continue until that record exists or Underwriting confirms the dates.';
      showNotification(msg, 'error', 16000);
      throw new Error(msg);
    }
    const earliestStart = getTpoExtendEarliestStartYmd(seed.firstSegmentStartDate);
    if (compareYmd(earliestStart, portalAnnualEnd) > 0) {
      const msg =
        'The annual policy end on file is before the earliest allowed extension start. This extension cannot continue in the app until Underwriting corrects the policy dates.';
      showNotification(msg, 'error', 14000);
      throw new Error(msg);
    }
    const coreInferred =
      inferredClass || inferExtendableVehicleClass(selectedPolicy) || null;
    const extensionVehicleClass = resolveExtensionVehicleClass(
      seed.firstSegmentVehicleClass,
      coreInferred,
      tpoExtendClasses
    );

    setSelectedCover('TPO_EXTEND');
    setSelectedClass(extensionVehicleClass);
    // Prefer calendar earliest (today when first-segment cert expired). Ignore blind tomorrow from finder.
    const fromFinder = transactionStartDate || null;
    const candidate =
      fromFinder && compareYmd(fromFinder, earliestStart) >= 0 ? fromFinder : earliestStart;
    const initialStart =
      compareYmd(candidate, portalAnnualEnd) > 0 ? portalAnnualEnd : candidate;
    setStartDate(initialStart);
    setCoverPeriod('1 Year (50%)');
    setEndDate(portalAnnualEnd);
    setInsuredData(seed.insuredData);
    const seededInstitutionType =
      extensionVehicleClass === 'Motor Commercial Institutional' ? seed.institutionType || null : null;
    setInstitutionType(seededInstitutionType);
    setVehicleData(
      seededInstitutionType
        ? { ...seed.vehicleData, institutionType: seededInstitutionType }
        : seed.vehicleData
    );
    setExtensionHydrationError(null);
    setExtensionContext({
      policyNo: sourcePolicyNo,
      sourcePolicy: selectedPolicy || null,
      seedClientId: seed.clientId || null,
      annualEndDate: portalAnnualEnd,
      firstSegmentStartDate: seed.firstSegmentStartDate || null,
      firstSegmentVehicleClass: seed.firstSegmentVehicleClass || extensionVehicleClass
    });
    setCurrentStep('cover-details');
  };

  useEffect(() => {
    if (currentStep !== 'extension-loading' || !extensionContext?.policyNo) return;
    let cancelled = false;

    const hydrate = async () => {
      try {
        const seed = await fetchExtensionSeed(getApiUrl, extensionContext.policyNo);
        if (cancelled) return;
        let seededVehicle = seed.vehicleData;
        const registrationNumber = seededVehicle?.registrationNumber || vehicleData?.registrationNumber;

        if (registrationNumber) {
          const vsRes = await fetch(getApiUrl('/api/dmvic/search-vehicle'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ registrationNumber })
          });
          const vs = await vsRes.json().catch(() => null);
          if (!cancelled && vsRes.ok && vs?.success && vs?.vehicle) {
            const v = vs.vehicle;
            seededVehicle = {
              ...(seededVehicle || {}),
              registrationNumber: (v.VehicleRegistrationNumber && String(v.VehicleRegistrationNumber).trim()) || seededVehicle?.registrationNumber || registrationNumber,
              chassisNumber: (v.ChassisNumber && String(v.ChassisNumber).trim()) || seededVehicle?.chassisNumber || '',
              engineNumber: (v.EngineNumber && String(v.EngineNumber).trim()) || seededVehicle?.engineNumber || ''
            };
          }
        }

        if (cancelled) return;
        setInsuredData(seed.insuredData);
        setVehicleData(seededVehicle || seed.vehicleData);
        const portalAnnualFromSeed =
          (seed.computedAnnualEndDate && String(seed.computedAnnualEndDate).trim()) ||
          (seed.firstSegmentStartDate && inclusiveEndForCoverPeriod(seed.firstSegmentStartDate, '1 Year')) ||
          '';
        if (seed.firstSegmentVehicleClass) {
          setSelectedClass(
            resolveExtensionVehicleClass(
              seed.firstSegmentVehicleClass,
              extensionContext?.firstSegmentVehicleClass || selectedClass,
              tpoExtendClasses
            )
          );
        }
        setExtensionContext((prev) =>
          prev && String(prev.policyNo || '') === String(seed.policyNo || '')
            ? {
                ...prev,
                seedClientId: seed.clientId || prev.seedClientId,
                firstSegmentStartDate: seed.firstSegmentStartDate || prev.firstSegmentStartDate,
                firstSegmentVehicleClass:
                  seed.firstSegmentVehicleClass || prev.firstSegmentVehicleClass,
                annualEndDate: portalAnnualFromSeed || prev.annualEndDate
              }
            : prev
        );
        if (portalAnnualFromSeed) {
          setEndDate(portalAnnualFromSeed);
        }

        const vdForDi = seededVehicle || seed.vehicleData || {};
        const regDi = String(vdForDi.registrationNumber || '').trim();
        const chDi = String(vdForDi.chassisNumber || '').trim();
        const startDi = String(startDate || '').trim().slice(0, 10);
        const endDi = String(portalAnnualFromSeed || extensionContext?.annualEndDate || '').trim().slice(0, 10);
        if (regDi && startDi && endDi && !chDi) {
          if (cancelled) return;
          setExtensionHydrationError(
            'Chassis number is required for overlap check and issuance. It was not returned from NTSA for this registration — try again or contact support.'
          );
          return;
        }
        if (regDi && chDi && startDi && endDi) {
          const diRes = await fetch(getApiUrl('/api/dmvic/validate-double-insurance'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              vehicleData: { chassisNumber: chDi, registrationNumber: regDi },
              policyDates: { startDate: startDi, endDate: endDi }
            })
          });
          const di = await diRes.json().catch(() => ({}));
          if (cancelled) return;
          if (diRes.ok && di.success && di.hasDoubleInsurance && !di.canProceed) {
            const hint = di.nextAvailableDate ? ` You may start on or after ${String(di.nextAvailableDate).slice(0, 10)}.` : '';
            setExtensionHydrationError(
              `${di.message || 'Double insurance was detected for this vehicle over the extension window.'}${hint} Adjust the extension start in Cover Details or resolve the conflicting policy.`
            );
            return;
          }
        }

        setExtensionHydrationError(null);
        setCurrentStep('checkout');
      } catch (e) {
        if (cancelled) return;
        setExtensionHydrationError(e.message || EXTENSION_SEED_USER_MESSAGE);
      }
    };

    hydrate();
    return () => { cancelled = true; };
  }, [currentStep, extensionContext?.policyNo, extensionContext?.annualEndDate, startDate]);

  const retryFailedSave = () => {
    const pendingData = localStorage.getItem('bimabot_pending_application');
    if (pendingData) {
      try {
        showNotification('Retrying to save your policy...', 'info');
        showNotification('Retry functionality will be available soon', 'info');
      } catch (error) {
        showNotification('Unable to retry save. Please contact support.', 'error');
      }
    } else {
      showNotification('No pending data found to retry.', 'warning');
    }
  };

  // Called by Checkout when payment + certificate are done. Checkout already saved application (with agentId/agency) and passes clientId.
  const handlePaymentComplete = (result) => {
    if (result?.clientId) {
      setClientId(result.clientId);
    }
    setCurrentStep('summary');
    if (onPurchaseComplete) {
      onPurchaseComplete(result || { clientId });
    }
  };

  const resetToStart = () => {
    setSelectedCover(null);
    setSelectedClass(null);
    setInstitutionType(null);
    setStartDate('');
    setCoverPeriod('');
    setEndDate('');
    setCurrentStep(initialEntryStep);
    setInsuredData(null);
    setVehicleData(null);
    setInsuranceCheckResult(null);
    setClientId(null);
    setExtensionContext(null);
    setExtensionHydrationError(null);
  };

  useEffect(() => {
    if (renewalSeed?.coverType) return;
    setCurrentStep(initialEntryStep);
  }, [initialEntryStep, renewalSeed]);

  const handleBackToDashboard = () => {
    resetToStart();
    clearAgentTransactionContext();
    if (onClose) {
      onClose();
    }
  };

  const premium = selectedCover && selectedClass && coverPeriod 
    ? calculatePremium(selectedCover, selectedClass, coverPeriod, vehicleData?.tonnage, vehicleData?.licensedToCarry, vehicleData?.bodyType, institutionType || vehicleData?.institutionType)
    : null;

  return (
    <div className="cover-purchase-modern">
      {/* Animated Background Elements */}
      <div className="cover-purchase-bg">
        <div className="bg-shape bg-shape-1"></div>
        <div className="bg-shape bg-shape-2"></div>
        <div className="bg-shape bg-shape-3"></div>
      </div>

      <div className="cover-purchase-wrapper">
        {/* Modern Header Section */}
        <div className="cover-purchase-header">
          <div className="header-content-modern">
            <div className="header-left-modern">
              <div className="header-icon-wrapper">
                <svg className="header-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="header-text">
                <h1 className="cover-purchase-title">Purchase Insurance Cover</h1>
                <p className="cover-purchase-subtitle">
                  {onBehalfOfLabel
                    ? (isDirectBusiness || agentTransactionContext?.isDirectBusiness
                      ? `Direct business — ${onBehalfOfLabel}`
                      : `Issuing on behalf of: ${onBehalfOfLabel}`)
                    : 'Select the perfect coverage for your needs'}
                </p>
              </div>
            </div>
            <button 
              onClick={handleBackToDashboard}
              className="back-button-modern"
              aria-label={onBehalfOfLabel ? 'Back to business type selection' : 'Back to Dashboard'}
            >
              <svg className="back-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              <span>{onBehalfOfLabel ? 'Back to business type selection' : 'Back to Dashboard'}</span>
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="cover-purchase-content">
          {currentStep === 'product-selection' && (
            <div className="product-selection-modern">
              <div className="selection-header">
                <h2 className="selection-title">
                  {comprehensiveOnly ? 'Comprehensive Insurance' : 'Choose Your Insurance Cover'}
                </h2>
                <p className="selection-description">
                  {comprehensiveOnly
                    ? 'Pilot Comprehensive cover — 1 month · pay 30% of annual · age ≤ 15 · no DMVIC · no Core. Same purchase flow as TPO/TOR.'
                    : 'Select the type of insurance coverage that best fits your needs. Both options provide comprehensive protection.'}
                </p>
              </div>
              
              <div className="product-cards-modern">
                {!comprehensiveOnly && (
                  <>
                <ProductCard
                  title="Third Party Only Insurance"
                  subtitle="TPO Cover"
                  features={[
                    "Third Party Liability Coverage",
                    "PLL for PSV & Commercial",
                    "Affordable Premiums"
                  ]}
                  onClick={() => handleCoverSelection('TPO')}
                  icon="🛡️"
                  isPremium={false}
                />
                <ProductCard
                  title="Time on Risk Insurance"
                  subtitle="TOR Cover"
                  features={[
                    "Third Party Liability Coverage",
                    "PLL for PSV & Commercial",
                    "Short period rates"
                  ]}
                  onClick={() => handleCoverSelection('TOR')}
                  icon="⏰"
                  isPremium={false}
                />
                  </>
                )}
                {showComprehensiveTile && (
                  <ProductCard
                    title="Comprehensive Insurance"
                    subtitle="COMP Cover — 1 Month"
                    features={[
                      "1 Month cover only",
                      "Pay 30% of annual premium",
                      "Optional EP / PVT / Windscreen / Radio"
                    ]}
                    onClick={() => handleCoverSelection('COMP')}
                    icon="🚗"
                    isPremium={false}
                  />
                )}
                {/*
                <ProductCard
                  title="Policy Extension"
                  subtitle="Extend Existing Policy"
                  features={[
                    "Search existing policy by registration",
                    "Reuse original policy number",
                    "Checkout with prefilled details"
                  ]}
                  onClick={() => setCurrentStep('extension-finder')}
                  icon="🔁"
                  isPremium={false}
                />
                */}
              </div>
            </div>
          )}

          {currentStep === 'extension-finder' && (
            <div className="product-selection-modern">
              <div className="selection-header">
                <h2 className="selection-title">Buy/Extend Policy</h2>
              </div>
              <div style={{ width: '100%', maxWidth: 720, margin: '0 auto' }}>
                <ExtendPolicyFinder onProceed={handleProceedFromCoreSearch} />
                <div style={{ marginTop: 16 }}>
                  <ProductCard
                    title="Start a new extendable cover purchase"
                    subtitle="New quote — not the second 50% on an existing portal policy"
                    features={[
                      'Use when you do not yet have a policy number from this app for this vehicle',
                      'You will enter vehicle and insured details manually; DMVIC search is still available later'
                    ]}
                    onClick={() => handleCoverSelection('TPO_EXTEND')}
                    icon="📋"
                    isPremium={false}
                    variant="extend"
                    buttonLabel="Get Cover"
                  />
                </div>
              </div>
            </div>
          )}

          {currentStep === 'cover-details' && selectedCover && (
            <CoverDetails
              coverType={selectedCover}
              classes={
                selectedCover === 'TPO'
                  ? tpoClasses
                  : selectedCover === 'TPO_EXTEND'
                    ? tpoExtendClasses
                    : selectedCover === 'COMP'
                      ? compClasses
                      : torClasses
              }
              pricing={
                selectedCover === 'TOR'
                  ? torPricing
                  : selectedCover === 'TPO_EXTEND' && !extensionContext?.annualEndDate
                    ? tpoExtendFreshMonthPricing
                    : selectedCover === 'TPO_EXTEND'
                      ? tpoExtendPricing
                      : selectedCover === 'COMP'
                        ? []
                        : tpoPricing
              }
              fixedPeriod={
                selectedCover === 'COMP'
                  ? '1 Month'
                  : selectedCover === 'TPO_EXTEND'
                    ? (extensionContext?.annualEndDate ? '1 Year (50%)' : '1 Month')
                    : null
              }
              initialData={{ selectedClass, startDate, coverPeriod, endDate, institutionType }}
              onComplete={handleCoverDetailsComplete}
              lockFields={selectedCover === 'TPO_EXTEND' && !!extensionContext}
              continueLabel={selectedCover === 'TPO_EXTEND' && !!extensionContext ? 'Continue to Checkout →' : 'Continue to Insured Details →'}
              extensionAnnualEndDate={selectedCover === 'TPO_EXTEND' ? extensionContext?.annualEndDate : null}
              extensionFirstSegmentStartDate={
                selectedCover === 'TPO_EXTEND' ? extensionContext?.firstSegmentStartDate : null
              }
              renewalEarliestStart={renewalSeed?.renewal?.earliestStart || null}
              onBack={() => {
                if (renewalSeed) {
                  if (onClose) onClose();
                  return;
                }
                setCurrentStep(selectedCover === 'TPO_EXTEND' ? 'extension-finder' : 'product-selection');
              }}
            />
          )}

          {currentStep === 'insured-details' && (
            <InsuredDetails
              coverType={selectedCover}
              selectedClass={selectedClass}
              startDate={startDate}
              coverPeriod={coverPeriod}
              endDate={endDate}
              initialData={insuredData}
              onComplete={handleInsuredDetailsComplete}
              onBack={() => setCurrentStep('cover-details')}
            />
          )}

          {currentStep === 'vehicle-details' && (
            <VehicleDetails
              coverType={selectedCover}
              selectedClass={selectedClass}
              institutionType={institutionType}
              startDate={startDate}
              endDate={endDate}
              coverPeriod={coverPeriod}
              pricing={
                selectedCover === 'TOR'
                  ? torPricing
                  : selectedCover === 'TPO_EXTEND' && !extensionContext?.annualEndDate
                    ? tpoExtendFreshMonthPricing
                    : selectedCover === 'TPO_EXTEND'
                      ? tpoExtendPricing
                      : tpoPricing
              }
              initialData={vehicleData}
              onComplete={handleVehicleDetailsComplete}
              onBack={() => setCurrentStep('insured-details')}
              onBackToCoverDetails={() => setCurrentStep('cover-details')}
              extensionAnnualEndDate={selectedCover === 'TPO_EXTEND' ? extensionContext?.annualEndDate : null}
            />
          )}

          {currentStep === 'checkout' && (
            <Checkout
              coverType={selectedCover}
              selectedClass={selectedClass}
              institutionType={institutionType || vehicleData?.institutionType || null}
              startDate={startDate}
              endDate={
                selectedCover === 'TPO_EXTEND' && extensionContext?.annualEndDate
                  ? extensionContext.annualEndDate
                  : endDate
              }
              coverPeriod={coverPeriod}
              insuredData={insuredData}
              vehicleData={vehicleData}
              premium={premium}
              agentId={agentId}
              agency={agency}
              agentUserId={agentUserId}
              agentTransactionContext={agentTransactionContext}
              isUnderwriting={!!issuedByAdminId}
              onBack={() => setCurrentStep(selectedCover === 'TPO_EXTEND' ? 'cover-details' : 'vehicle-details')}
              extensionContext={extensionContext}
              extensionSeedBlocked={
                selectedCover === 'TPO_EXTEND' &&
                !!extensionContext?.policyNo &&
                (!insuredData?.idNumber || !String(vehicleData?.registrationNumber || '').trim())
              }
              renewalContext={renewalSeed?.renewal || null}
              onPaymentComplete={(result) => {
                clearAgentTransactionContext();
                handlePaymentComplete(result);
              }}
            />
          )}

          {currentStep === 'extension-loading' && (
            <div className="cover-details" style={{ textAlign: 'center', padding: '60px 20px', maxWidth: 560, margin: '0 auto' }}>
              <h2 style={{ marginBottom: 12 }}>{extensionHydrationError ? 'Could not load extension data' : 'Loading extension data...'}</h2>
              {!extensionHydrationError && (
                <>
                  <p style={{ marginBottom: 10, color: '#556' }}>
                    Fetching insured and vehicle details from our records for checkout.
                  </p>
                  <div style={{ fontSize: 28, lineHeight: 1 }}>⏳</div>
                </>
              )}
              {extensionHydrationError && (
                <p style={{ marginTop: 16, color: '#5d1a1a', fontSize: 14, lineHeight: 1.5, textAlign: 'left' }}>
                  {extensionHydrationError}
                </p>
              )}
              {extensionHydrationError && (
                <button
                  type="button"
                  className="btn-primary"
                  style={{ marginTop: 20 }}
                  onClick={() => {
                    setExtensionHydrationError(null);
                    setCurrentStep('cover-details');
                  }}
                >
                  Back to cover details
                </button>
              )}
            </div>
          )}

          {currentStep === 'summary' && clientId && (
            <Receipt
              coverType={selectedCover}
              selectedClass={selectedClass}
              startDate={startDate}
              endDate={endDate}
              coverPeriod={coverPeriod}
              insuredData={insuredData}
              vehicleData={vehicleData}
              premium={premium}
              clientId={clientId}
              onNewApplication={() => {
                resetToStart();
                if (onClose) onClose();
              }}
              onRetrySave={retryFailedSave}
            />
          )}
        </div>
      </div>

      {/* Snackbar Notifications */}
      {showSnackbar && (
        <Snackbar
          message={snackbarMessage}
          type={snackbarType}
          onClose={() => setShowSnackbar(false)}
        />
      )}
    </div>
  );
};

export default CoverPurchase;
